/* =============================================================================
   Demo datasets
   -----------------------------------------------------------------------------
   Domain data for a residential construction investor platform. The contractor
   roster is deliberately shaped to exercise EVERY filter type the table
   contract supports (text, money, moneyRange, percentage, percentageRange,
   number, numberRange, date, dateRange, datetime, datetimeRange, select), and
   the sizes are chosen so pagination, sorting and aggregation all have
   something real to do.
   ============================================================================= */

import type {
  NotificationItem,
  Project,
  ProjectUpdate,
  Row,
  SchedulePhase,
  RemoteOption,
} from './types';

/* -----------------------------------------------------------------------------
   Contractors — the primary table dataset
   -------------------------------------------------------------------------- */

export interface ContractorRow extends Row {
  id: string;
  name: string;
  trade: string;
  phase: string;
  status: string;
  contractValue: number;
  paidToDate: number;
  completion: number;
  crewSize: number;
  startDate: string;
  lastInspection: string;
  approvedAt: string;
  isVerified: boolean;
}

const TRADES = [
  'Sitework',
  'Concrete',
  'Framing',
  'Roofing',
  'Electrical',
  'Plumbing',
  'HVAC',
  'Drywall',
  'Cabinetry',
  'Flooring',
  'Painting',
  'Landscaping',
  'Windows',
  'Insulation',
  'Masonry',
] as const;

const PHASES = [
  'Planning and pre-construction',
  'Acquisition and mobilization',
  'Demolition and site prep',
  'Foundation',
  'Framing and roofing',
  'Utilities and enclosure',
  'Finishes',
  'Landscaping',
] as const;

const STATUSES = ['Awarded', 'In progress', 'On hold', 'Closed', 'Disputed'] as const;

const NAME_PARTS_A = [
  'Aldridge', 'Brennan', 'Calloway', 'Dunmore', 'Eastwick', 'Fairholm', 'Grantham',
  'Halloway', 'Ingersoll', 'Jarrett', 'Kingsley', 'Lindmark', 'Marchetti', 'Norwood',
  'Oakhurst', 'Pemberton', 'Quillan', 'Radcliffe', 'Sandoval', 'Thornbury', 'Underhill',
  'Vasquez', 'Whitlock', 'Yarborough', 'Ashcombe', 'Blackwell', 'Cortez', 'Delgado',
  'Ellsworth', 'Fenwick', 'Gallagher', 'Hensley', 'Iverson', 'Kowalski', 'Lattimore',
  'Merrick', 'Navarro', 'Ortega', 'Prescott', 'Rowntree', 'Stavros', 'Taliaferro',
  'Uhlmann', 'Vandermeer', 'Wexford', 'Zamora',
];

const NAME_PARTS_B = [
  'Construction', 'Builders', 'Contracting', 'Works', 'Group', '& Sons', 'Partners',
  'Industries', 'Services', '& Co.',
];

/**
 * Deterministic pseudo-random generator. A fixed seed keeps the fixture stable
 * across reloads and between the showcase and the Storybook stories, so a
 * screenshot comparison is meaningful.
 */
function makeRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}

function isoDate(year: number, month: number, day: number, hour = 0, minute = 0): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:00`;
}

function buildContractors(count: number): ContractorRow[] {
  const rand = makeRandom(20260918);
  const rows: ContractorRow[] = [];

  for (let i = 0; i < count; i += 1) {
    const trade = TRADES[i % TRADES.length];
    const phase = PHASES[i % PHASES.length];
    const status = STATUSES[Math.floor(rand() * STATUSES.length)] ?? 'In progress';

    const contractValue = Math.round((28_000 + rand() * 470_000) / 100) * 100;
    // A closed contract is fully paid; everything else is somewhere along the way.
    const paidRatio =
      status === 'Closed' ? 1 : status === 'On hold' ? 0.2 + rand() * 0.25 : 0.25 + rand() * 0.7;
    const paidToDate = Math.round(contractValue * Math.min(paidRatio, 1) * 100) / 100;
    const completion =
      status === 'Closed' ? 100 : Math.round(Math.min(paidRatio * 100 + rand() * 12, 99));

    const startMonth = 1 + Math.floor(rand() * 10);
    const startDay = 1 + Math.floor(rand() * 27);
    const inspectMonth = Math.min(startMonth + 1 + Math.floor(rand() * 4), 12);
    const inspectDay = 1 + Math.floor(rand() * 27);

    rows.push({
      id: `CTR-${String(1000 + i)}`,
      name: `${NAME_PARTS_A[i % NAME_PARTS_A.length]} ${NAME_PARTS_B[i % NAME_PARTS_B.length]}`,
      trade,
      phase,
      status,
      contractValue,
      paidToDate,
      completion,
      crewSize: 2 + Math.floor(rand() * 38),
      startDate: isoDate(2026, startMonth, startDay),
      lastInspection: isoDate(2026, inspectMonth, inspectDay),
      approvedAt: isoDate(2026, inspectMonth, inspectDay, 8 + Math.floor(rand() * 9), Math.floor(rand() * 60)),
      isVerified: status !== 'Disputed' && rand() > 0.18,
    });
  }

  return rows;
}

export const CONTRACTORS: ContractorRow[] = buildContractors(84);

/* -----------------------------------------------------------------------------
   Remote-search option source (for the async combobox)
   -------------------------------------------------------------------------- */

export const CONTRACTOR_OPTIONS: RemoteOption[] = CONTRACTORS.map((row) => ({
  value: row.id,
  label: row.name,
  description: `${row.trade} · ${row.phase}`,
}));

export const CITY_OPTIONS: RemoteOption[] = [
  'Austin', 'Round Rock', 'Cedar Park', 'Georgetown', 'Pflugerville', 'Leander',
  'Kyle', 'Buda', 'San Marcos', 'Dripping Springs', 'Lakeway', 'Bee Cave',
  'Manor', 'Hutto', 'Taylor', 'Bastrop', 'Elgin', 'Liberty Hill', 'Wimberley',
  'Marble Falls', 'Smithville', 'Lockhart', 'Lago Vista', 'Sunset Valley',
].map((city) => ({ value: city.toLowerCase().replace(/\s+/g, '-'), label: city }));

/* -----------------------------------------------------------------------------
   Projects
   -------------------------------------------------------------------------- */

export const PROJECTS: Project[] = [
  {
    id: 'lakeview',
    name: 'Lakeview Residence',
    address: '1140 Lakeshore Bend',
    city: 'Austin, TX',
    lot: 'Lot 214',
    ownershipShare: 34,
    phase: 'Finishes',
    progress: 68,
    contributed: 1_240_000,
    actual: 1_084_500,
    balance: 155_500,
    projectedSale: '2027-03-01',
    updatedAt: '2026-09-18',
  },
  {
    id: 'maple-court',
    name: 'Maple Court Residence',
    address: '88 Maple Court',
    city: 'Round Rock, TX',
    lot: 'Lot 88',
    ownershipShare: 22,
    phase: 'Framing and roofing',
    progress: 41,
    contributed: 860_000,
    actual: 702_000,
    balance: 158_000,
    projectedSale: '2027-11-01',
    updatedAt: '2026-09-04',
  },
  {
    id: 'birch-hollow',
    name: 'Birch Hollow Residence',
    address: '41 Birch Hollow Trail',
    city: 'Cedar Park, TX',
    lot: 'Lot 41',
    ownershipShare: 18,
    phase: 'Foundation',
    progress: 12,
    contributed: 410_000,
    actual: 96_000,
    balance: 314_000,
    projectedSale: '2028-06-01',
    updatedAt: '2026-08-21',
  },
];

/* -----------------------------------------------------------------------------
   Schedule — Lakeview. Spans are percentages of the 12-month axis, and the
   in-progress phases deliberately overlap, which is the whole point of the
   schedule component.
   -------------------------------------------------------------------------- */

export const SCHEDULE: SchedulePhase[] = [
  { id: 'planning', name: 'Planning and pre-construction', plannedStart: 0, plannedWidth: 22, actualStart: 0, actualWidth: 20, status: 'completed', budgeted: 96_000, actual: 91_400 },
  { id: 'acquisition', name: 'Acquisition and mobilization', plannedStart: 18, plannedWidth: 12, actualStart: 18, actualWidth: 13, status: 'completed', budgeted: 285_000, actual: 283_900 },
  { id: 'demolition', name: 'Demolition and site prep', plannedStart: 28, plannedWidth: 11, actualStart: 29, actualWidth: 12, status: 'completed', budgeted: 118_000, actual: 121_700 },
  { id: 'foundation', name: 'Foundation', plannedStart: 36, plannedWidth: 17, actualStart: 35, actualWidth: 18, status: 'completed', budgeted: 242_000, actual: 231_500 },
  { id: 'framing', name: 'Framing and roofing', plannedStart: 52, plannedWidth: 20, actualStart: 51, actualWidth: 19, status: 'completed', budgeted: 298_000, actual: 269_600 },
  { id: 'utilities', name: 'Utilities and enclosure', plannedStart: 66, plannedWidth: 30, actualStart: 66, actualWidth: 24, status: 'in-progress', budgeted: 186_000, actual: 86_400 },
  { id: 'finishes', name: 'Finishes', plannedStart: 77, plannedWidth: 34, actualStart: 77, actualWidth: 18, status: 'in-progress', budgeted: 312_000, actual: 0 },
  { id: 'landscaping', name: 'Landscaping', plannedStart: 88, plannedWidth: 12, actualStart: null, actualWidth: null, status: 'planned', budgeted: 84_000, actual: 0 },
  { id: 'completion', name: 'Completion and sale', plannedStart: 90, plannedWidth: 10, actualStart: null, actualWidth: null, status: 'planned', budgeted: 0, actual: 0 },
];

/* -----------------------------------------------------------------------------
   Published investor updates
   -------------------------------------------------------------------------- */

export const UPDATES: ProjectUpdate[] = [
  {
    id: 'u-2026-09-18',
    date: '2026-09-18',
    title: 'Framing complete and inspection approved',
    body: 'Structure and roofing closed on schedule. The municipal inspection passed with no findings, and the phase settled $28,400 under the figure published in June.',
    photos: 12,
    documents: 3,
    approvedBy: 'Finance',
  },
  {
    id: 'u-2026-09-04',
    date: '2026-09-04',
    title: 'Electrical and plumbing started in parallel',
    body: 'Two work fronts opened alongside the finishes on the upper floor. Materials were delivered and checked against the invoice before anything was posted.',
    photos: 8,
    documents: 2,
    approvedBy: 'Finance',
  },
  {
    id: 'u-2026-08-21',
    date: '2026-08-21',
    title: 'Roofing finished and water test passed',
    body: 'Roofing completed nine days ahead of the forecast published in June. The water tightness test passed on the first attempt.',
    photos: 9,
    documents: 1,
    approvedBy: 'Project owner',
  },
  {
    id: 'u-2026-08-07',
    date: '2026-08-07',
    title: 'Window order confirmed for early October',
    body: 'The window package was released to the supplier with a confirmed delivery window. Installation is scheduled to begin in the first week of October.',
    photos: 4,
    documents: 2,
    approvedBy: 'Project owner',
  },
  {
    id: 'u-2026-07-24',
    date: '2026-07-24',
    title: 'Framing topped out',
    body: 'The final truss was set and the structure is fully framed. Rough-in for the mechanical systems begins next week.',
    photos: 15,
    documents: 1,
    approvedBy: 'Finance',
  },
];

/* -----------------------------------------------------------------------------
   Chart series
   -------------------------------------------------------------------------- */

/** Monthly spend against the phased budget, in dollars. */
export const SPEND_BY_MONTH = [
  { month: '2026-01', label: 'Jan', budgeted: 62_000, actual: 58_400 },
  { month: '2026-02', label: 'Feb', budgeted: 88_000, actual: 91_200 },
  { month: '2026-03', label: 'Mar', budgeted: 134_000, actual: 128_900 },
  { month: '2026-04', label: 'Apr', budgeted: 118_000, actual: 121_700 },
  { month: '2026-05', label: 'May', budgeted: 142_000, actual: 134_300 },
  { month: '2026-06', label: 'Jun', budgeted: 186_000, actual: 168_400 },
  { month: '2026-07', label: 'Jul', budgeted: 210_000, actual: 197_800 },
  { month: '2026-08', label: 'Aug', budgeted: 198_000, actual: 121_300 },
  { month: '2026-09', label: 'Sep', budgeted: 176_000, actual: 62_500 },
];

/**
 * Budgeted against actual per phase — drives the bar chart and the gauge.
 *
 * The phase id rides along with the two names because the bar chart draws its
 * axis through the catalogue: a name is a word and a word has to be resolved per
 * language at render, while the id is what both catalogues are keyed by. The
 * names stay for the fixtures' own readers — the story asserts on them.
 */
export const PHASE_BUDGET = SCHEDULE.filter((p) => p.budgeted > 0).map((p) => ({
  phaseId: p.id,
  phase: p.name,
  short: p.name.split(' ')[0] ?? p.name,
  budgeted: p.budgeted,
  actual: p.actual,
  status: p.status,
}));

/** Capital contributed across the three holdings — pie chart. */
export const SHARE_BY_PROJECT = PROJECTS.map((p) => ({
  name: p.name.replace(' Residence', ''),
  value: p.contributed,
  share: p.ownershipShare,
}));

/** Inspections passed against inspections scheduled, per month — domain chart. */
export const INSPECTIONS_BY_MONTH = [
  { month: '2026-04', label: 'Apr', passed: 6, scheduled: 7 },
  { month: '2026-05', label: 'May', passed: 9, scheduled: 9 },
  { month: '2026-06', label: 'Jun', passed: 11, scheduled: 12 },
  { month: '2026-07', label: 'Jul', passed: 14, scheduled: 14 },
  { month: '2026-08', label: 'Aug', passed: 12, scheduled: 13 },
  { month: '2026-09', label: 'Sep', passed: 8, scheduled: 10 },
];

/** Hours logged per trade per month — the domain data chart. */
export const LABOUR_BY_TRADE = [
  { trade: 'Framing', '2026-07': 1_840, '2026-08': 2_120, '2026-09': 940 },
  { trade: 'Electrical', '2026-07': 620, '2026-08': 1_180, '2026-09': 1_360 },
  { trade: 'Plumbing', '2026-07': 540, '2026-08': 980, '2026-09': 1_240 },
  { trade: 'HVAC', '2026-07': 0, '2026-08': 460, '2026-09': 880 },
  { trade: 'Drywall', '2026-07': 0, '2026-08': 240, '2026-09': 1_420 },
  { trade: 'Roofing', '2026-07': 1_240, '2026-08': 380, '2026-09': 0 },
];

/** Cumulative cash flow, signed — drives the area/flow line. */
export const CASH_FLOW = [
  { month: '2026-01', label: 'Jan', contributed: 180_000, spent: 58_400 },
  { month: '2026-02', label: 'Feb', contributed: 240_000, spent: 91_200 },
  { month: '2026-03', label: 'Mar', contributed: 260_000, spent: 128_900 },
  { month: '2026-04', label: 'Apr', contributed: 180_000, spent: 121_700 },
  { month: '2026-05', label: 'May', contributed: 160_000, spent: 134_300 },
  { month: '2026-06', label: 'Jun', contributed: 140_000, spent: 168_400 },
  { month: '2026-07', label: 'Jul', contributed: 80_000, spent: 197_800 },
  { month: '2026-08', label: 'Aug', contributed: 0, spent: 121_300 },
  { month: '2026-09', label: 'Sep', contributed: 0, spent: 62_500 },
];

/* -----------------------------------------------------------------------------
   Notifications
   -------------------------------------------------------------------------- */

export const NOTIFICATIONS: NotificationItem[] = [
  {
    id: 'n-1',
    category: 'success',
    title: 'Framing phase closed under budget',
    description: 'Lakeview settled $28,400 under the published figure. The update is now live in the portal.',
    timestamp: '2026-09-18T09:12:00',
    read: false,
  },
  {
    id: 'n-2',
    category: 'warning',
    title: 'Inspection scheduled for Oct 02',
    description: 'Window installation inspection is booked. Two documents are still awaiting sign-off before publication.',
    timestamp: '2026-09-17T15:40:00',
    read: false,
  },
  {
    id: 'n-3',
    category: 'info',
    title: 'Three expense records need verification',
    description: 'Maple Court has three expenses posted without a matching invoice.',
    timestamp: '2026-09-16T11:05:00',
    read: false,
  },
  {
    id: 'n-4',
    category: 'error',
    title: 'Insulation contract expired',
    description: 'The insulation award for Birch Hollow lapsed on Sep 12 and needs to be re-issued.',
    timestamp: '2026-09-12T08:30:00',
    read: true,
  },
  {
    id: 'n-5',
    category: 'success',
    title: 'Electrical and plumbing started',
    description: 'Two work fronts opened in parallel. Eight photos were attached to the phase.',
    timestamp: '2026-09-04T14:22:00',
    read: true,
  },
  {
    id: 'n-6',
    category: 'info',
    title: 'New investor added to Maple Court',
    description: 'A 9% ownership share was recorded and the participation agreement filed.',
    timestamp: '2026-08-29T16:48:00',
    read: true,
  },
];

/* -----------------------------------------------------------------------------
   Avatar demo data
   -------------------------------------------------------------------------- */

export const PEOPLE = [
  { name: 'Rafael Menezes', role: 'Investor' },
  { name: 'Ana Belmonte', role: 'Finance' },
  { name: 'Curtis Halloway', role: 'Site lead' },
  { name: 'Priya Raman', role: 'Coordinator' },
  { name: 'Tomas Eklund', role: 'Approver' },
  { name: 'Nadia Osei', role: 'Investor' },
  { name: 'Jonah Whitlock', role: 'Field' },
  { name: 'Sofia Marchetti', role: 'Back office' },
] as const;
